<script>
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import SuccessBanner from '../components/SuccessBanner.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import { smtpApi } from '../stores/api.js';
  import { activeCompanyId } from '../stores/company.js';
  import { capabilities, refreshCapabilities } from '../stores/capabilities.js';
  import { normalizeEmailAddress, isEmptyOrValidEmail } from '../lib/email-address.js';
  import { formatDateTime } from '../lib/labels.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { Mail, Save, Trash2, Send } from '../lib/icons/index.js';

  // Impostazioni › Email: the company's SMTP server, for the courtesy copy.
  // Shown only to a company admin (capabilities.canManageSmtp); the server
  // refuses everyone else regardless.
  export let embedded = false;

  const PORTS = [587, 465, 25, 2525];

  let settings = null;
  let loading = false;
  let saving = false;
  let testing = false;
  let error = '';
  let success = '';
  let testOutcome = null;
  let form = emptyForm();
  let testRecipient = '';
  /** The company the loaded state belongs to: a late answer for another is dropped. */
  let loadedFor = null;
  /** The company a load was started for — once per company, even when it fails. */
  let requestedFor = null;

  function emptyForm() {
    return {
      host: '', port: 587, username: '', password: '', clearPassword: false,
      fromAddress: '', fromName: '', replyTo: '', enabled: false,
    };
  }

  function formFrom(s) {
    return {
      host: s?.host || '',
      port: s?.port || 587,
      username: s?.username || '',
      password: '',
      clearPassword: false,
      fromAddress: s?.fromAddress || '',
      fromName: s?.fromName || '',
      replyTo: s?.replyTo || '',
      enabled: Boolean(s?.enabled),
    };
  }

  $: companyId = $activeCompanyId;
  $: canManage = $capabilities?.canManageSmtp === true;
  $: if (companyId !== loadedFor) reset(companyId);
  $: if (companyId && canManage && loadedFor === companyId && requestedFor !== companyId) {
    requestedFor = companyId;
    load(companyId);
  }

  // A company switch clears everything typed for the previous one, password included.
  function reset(id) {
    loadedFor = id;
    requestedFor = null;
    settings = null;
    form = emptyForm();
    testRecipient = '';
    error = '';
    success = '';
    testOutcome = null;
  }

  async function load(id) {
    loading = true;
    try {
      const value = await smtpApi.get(id);
      if (id !== loadedFor) return;
      settings = value;
      form = formFrom(value);
    } catch (err) {
      if (id === loadedFor) error = err.message;
    } finally {
      loading = false;
    }
  }

  $: security = form.port === 465 || form.port === '465' ? 'tls' : 'starttls';
  $: fromAddressInvalid = Boolean(form.fromAddress.trim()) && !normalizeEmailAddress(form.fromAddress);
  $: replyToInvalid = !isEmptyOrValidEmail(form.replyTo);
  $: testRecipientInvalid = !isEmptyOrValidEmail(testRecipient);

  function categoryText(category) {
    return tImmediate(`email.categories.${category}`, undefined, tImmediate('email.categories.unknown'));
  }

  function outcomeText(result, smtpCode) {
    const reason = categoryText(result);
    return smtpCode ? `${reason} (${tImmediate('email.smtpCode', { code: smtpCode })})` : reason;
  }

  async function save(event) {
    event.preventDefault();
    error = '';
    success = '';
    if (!form.host.trim()) { error = $t('email.validation.host'); return; }
    if (!normalizeEmailAddress(form.fromAddress)) { error = $t('email.validation.fromAddress'); return; }
    if (replyToInvalid) { error = $t('email.validation.replyTo'); return; }

    const payload = {
      host: form.host.trim(),
      port: Number(form.port),
      security,
      username: form.username.trim() || null,
      fromAddress: form.fromAddress.trim(),
      fromName: form.fromName.trim() || null,
      replyTo: form.replyTo.trim() || null,
      enabled: form.enabled,
    };
    // Write-only: left out keeps the stored password, '' clears it.
    if (form.clearPassword) payload.password = '';
    else if (form.password) payload.password = form.password;

    const id = companyId;
    saving = true;
    try {
      const saved = await smtpApi.save(id, payload);
      if (id !== loadedFor) return;
      settings = saved;
      form = formFrom(saved);
      success = $t('email.saved');
      refreshCapabilities();
    } catch (err) {
      if (id === loadedFor) error = err.message;
    } finally {
      saving = false;
    }
  }

  async function removeSettings() {
    if (!confirm($t('email.removeConfirm'))) return;
    error = '';
    success = '';
    const id = companyId;
    try {
      const empty = await smtpApi.remove(id);
      if (id !== loadedFor) return;
      settings = empty;
      form = formFrom(empty);
      testOutcome = null;
      success = $t('email.removed');
      refreshCapabilities();
    } catch (err) {
      if (id === loadedFor) error = err.message;
    }
  }

  async function sendTest() {
    error = '';
    testOutcome = null;
    if (testRecipientInvalid) { error = $t('email.validation.recipient'); return; }
    const id = companyId;
    testing = true;
    try {
      const outcome = await smtpApi.test(id, testRecipient.trim() || null);
      if (id !== loadedFor) return;
      testOutcome = outcome;
      settings = await smtpApi.get(id);
    } catch (err) {
      if (id === loadedFor) error = err.message;
    } finally {
      testing = false;
    }
  }
</script>

{#snippet body()}
  <div class="page-header mb-4">
    {#if !embedded}
      <div class="flex items-center gap-2">
        <Mail size={22} class="text-action" />
        <h1 class="section-title mb-0">{$t('settings.tabs.email')}</h1>
      </div>
    {/if}
    <p class="text-muted text-sm mt-1 max-w-2xl">{$t('email.description')}</p>
  </div>

  {#if !companyId}
    <div class="card p-8 text-center text-muted">{$t('paymentMethods.selectCompanyFirst')}</div>
  {:else if $capabilities === null}
    <PageLoader />
  {:else if !canManage}
    <div class="card p-8 text-center text-muted">{$t('email.notAllowed')}</div>
  {:else}
    <div class="card p-5 mb-4 measure">
      <h2 class="eyebrow eyebrow-lg mb-2" style="color: var(--color-primary);">{$t('email.howItWorksTitle')}</h2>
      <p class="text-sm mb-2">{$t('email.howItWorks1')}</p>
      <p class="text-sm mb-2">{$t('email.howItWorks2')}</p>
      <p class="text-sm mb-2">{$t('email.howItWorks3')}</p>
      <p class="text-sm">{$t('email.howItWorks4')}</p>
    </div>

    {#if !$capabilities.smtpFeatureAvailable}
      <div class="alert alert-warning mb-4" role="status">
        <div>
          <p class="alert-title">{$t('email.featureUnavailableTitle')}</p>
          <p class="alert-message measure">{$t('email.featureUnavailable')}</p>
        </div>
      </div>
    {/if}

    <ErrorBanner {error} />
    <SuccessBanner message={success} />

    {#if loading}
      <PageLoader />
    {:else if settings === null}
      <!-- The load failed: the banner above says why. -->
    {:else}
      <div class="card p-5 mb-4">
        <div class="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h2 class="eyebrow eyebrow-lg" style="color: var(--color-primary);">{$t('settings.tabs.email')}</h2>
          {#if !settings.configured}
            <span class="badge badge-gray">{$t('email.statusNotConfigured')}</span>
          {:else if settings.enabled}
            <span class="badge badge-green">{$t('email.statusOn')}</span>
          {:else}
            <span class="badge badge-yellow">{$t('email.statusOff')}</span>
          {/if}
        </div>

        <form on:submit={save} class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <fieldset class="contents" disabled={!$capabilities.smtpFeatureAvailable}>
            <label class="flex flex-col md:col-span-2">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.host')} *<InfoHint text={$t('email.fields.hostHint')} /></span>
              <input id="smtp-host" class="input font-mono" required maxlength="253" autocomplete="off"
                bind:value={form.host} placeholder="smtp.tuodominio.it" />
            </label>
            <label class="flex flex-col">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.port')} *<InfoHint text={$t('email.fields.portHint')} /></span>
              <select id="smtp-port" class="input" bind:value={form.port}>
                {#each PORTS as port}<option value={port}>{port}</option>{/each}
              </select>
            </label>
            <div class="flex flex-col">
              <span class="eyebrow text-muted mb-1">{$t('email.fields.security')}</span>
              <p class="text-sm py-2" id="smtp-security">{$t(`email.security.${security}`)}</p>
            </div>
            <label class="flex flex-col">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.username')}<InfoHint text={$t('email.fields.usernameHint')} /></span>
              <input id="smtp-username" class="input" maxlength="256" autocomplete="off" bind:value={form.username} />
            </label>
            <label class="flex flex-col">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.password')}<InfoHint text={$t('email.fields.passwordHint')} /></span>
              <input id="smtp-password" class="input" type="password" maxlength="512" autocomplete="new-password"
                bind:value={form.password} disabled={form.clearPassword}
                placeholder={settings.passwordSet ? $t('email.fields.passwordSetPlaceholder') : ''} />
              {#if settings.passwordSet}
                <span class="flex items-center gap-2 mt-1 text-xs">
                  <input type="checkbox" bind:checked={form.clearPassword} />
                  {$t('email.fields.passwordClear')}
                </span>
              {/if}
            </label>
            <label class="flex flex-col">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.fromAddress')} *<InfoHint text={$t('email.fields.fromAddressHint')} /></span>
              <input id="smtp-from" class="input" type="email" required maxlength="254" bind:value={form.fromAddress}
                aria-invalid={fromAddressInvalid} placeholder="fatture@tuodominio.it" />
              {#if fromAddressInvalid}<p class="mt-1 text-xs text-warning" role="status">{$t('email.validation.fromAddress')}</p>{/if}
            </label>
            <label class="flex flex-col">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.fromName')}<InfoHint text={$t('email.fields.fromNameHint')} /></span>
              <input id="smtp-from-name" class="input" maxlength="128" bind:value={form.fromName} />
            </label>
            <label class="flex flex-col md:col-span-2">
              <span class="eyebrow text-muted mb-1 inline-flex items-center gap-1">{$t('email.fields.replyTo')}<InfoHint text={$t('email.fields.replyToHint')} /></span>
              <input id="smtp-reply-to" class="input" type="email" maxlength="254" bind:value={form.replyTo}
                aria-invalid={replyToInvalid} />
              {#if replyToInvalid}<p class="mt-1 text-xs text-warning" role="status">{$t('email.validation.replyTo')}</p>{/if}
            </label>
            <div class="md:col-span-2">
              <label class="flex items-center gap-2">
                <input id="smtp-enabled" type="checkbox" bind:checked={form.enabled} />
                <span class="text-sm font-bold">{$t('email.fields.enabled')}</span>
              </label>
              <p class="mt-1 text-xs text-muted measure">{$t('email.fields.enabledHint')}</p>
            </div>
            <div class="md:col-span-2 flex justify-end gap-2 flex-wrap">
              {#if settings.configured}
                <button type="button" class="btn btn-secondary btn-sm" on:click={removeSettings}>
                  <Trash2 size={14} class="mr-1" /> {$t('email.remove')}
                </button>
              {/if}
              <button id="smtp-save" type="submit" class="btn btn-accent btn-sm" disabled={saving}>
                <Save size={14} class="mr-1" /> {saving ? $t('email.saving') : $t('email.save')}
              </button>
            </div>
          </fieldset>
        </form>
      </div>

      {#if settings.configured}
        <div class="card p-5 mb-4">
          <h2 class="eyebrow eyebrow-lg mb-1" style="color: var(--color-primary);">{$t('email.test.title')}</h2>
          <p class="text-sm text-muted mb-3 measure">{$t('email.test.description')}</p>
          <div class="flex flex-col sm:flex-row gap-2 sm:items-end">
            <label class="flex flex-col flex-1">
              <span class="eyebrow text-muted mb-1">{$t('email.test.recipient')}</span>
              <input id="smtp-test-recipient" class="input" type="email" maxlength="254" bind:value={testRecipient}
                placeholder={$t('email.test.recipientPlaceholder')} aria-invalid={testRecipientInvalid} />
            </label>
            <button id="smtp-test" type="button" class="btn btn-secondary btn-sm"
              disabled={testing || !$capabilities.smtpFeatureAvailable} on:click={sendTest}>
              <Send size={14} class="mr-1" /> {testing ? $t('email.test.sending') : $t('email.test.send')}
            </button>
          </div>
          {#if testOutcome}
            {#if testOutcome.ok}
              <div class="mt-3"><SuccessBanner message={$t('email.test.ok', { recipient: testOutcome.recipient })} /></div>
            {:else}
              <p class="mt-3 text-sm text-danger" role="status" id="smtp-test-failed">
                {$t('email.test.failed', { reason: outcomeText(testOutcome.result, testOutcome.smtpCode) })}
              </p>
            {/if}
          {/if}
          <p class="mt-3 text-xs text-muted" id="smtp-last-test">
            {#if settings.lastTestAt}
              {$t('email.test.last', { when: formatDateTime(settings.lastTestAt), result: categoryText(settings.lastTestResult) })}
            {:else}
              {$t('email.test.never')}
            {/if}
          </p>
        </div>
      {/if}
    {/if}
  {/if}
{/snippet}

{#if embedded}
  {@render body()}
{:else}
  <Layout>
    {@render body()}
  </Layout>
{/if}
